import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeader } from '../../../services/otpAuthService';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

interface SearchResult {
  id: string;
  title: string;
  areaName: string;
  areaColor?: string;
  repliesCount: number;
  createdAt: string;
}

interface SearchBarProps {
  className?: string;
  initialQuery?: string;
}

function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 30) {
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } else if (diffDays > 0) {
    return `${diffDays} ${diffDays === 1 ? 'giorno' : 'giorni'} fa`;
  } else if (diffHours > 0) {
    return `${diffHours} ${diffHours === 1 ? 'ora' : 'ore'} fa`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minuti'} fa`;
  } else {
    return 'adesso';
  }
}

const highlightText = (text: string, q: string): React.ReactNode => {
  if (!q.trim()) return text;
  const words = q.trim().split(/\s+/).filter(w => w.length >= 2);
  if (words.length === 0) return text;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-700/60 text-inherit rounded-sm px-0.5">{part}</mark>
      : part
  );
};

const SearchBar: React.FC<SearchBarProps> = ({ className, initialQuery }) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    setOpen(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/community/v2/search?q=${encodeURIComponent(q)}&limit=8`,
        { headers: getAuthHeader() }
      );
      if (res.ok) {
        const data = await res.json();
        const raw = Array.isArray(data.data) ? data.data : Array.isArray(data.results) ? data.results : [];
        setResults(raw.map((r: any) => ({
          id: r._id || r.id,
          title: r.title,
          areaName: r.area?.displayName || '',
          areaColor: r.area?.color,
          repliesCount: r.repliesCount || 0,
          createdAt: r.createdAt,
        })));
      } else {
        setResults([]);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      search(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeDropdown]);

  const navigateToResult = useCallback((id: string) => {
    const hl = query.trim();
    closeDropdown();
    setQuery('');
    navigate(`/area-riservata/community-v2/discussione/${id}${hl ? `?hl=${encodeURIComponent(hl)}` : ''}`);
  }, [closeDropdown, navigate, query]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && focusedIndex >= 0 && results[focusedIndex]) {
        navigateToResult(results[focusedIndex].id);
      } else if (query.trim().length >= 2) {
        closeDropdown();
        navigate(`/area-riservata/community-v2/cerca?q=${encodeURIComponent(query.trim())}`);
      }
      return;
    }

    if (!open) return;

    if (e.key === 'Escape') {
      closeDropdown();
      inputRef.current?.blur();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, results.length - 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
      return;
    }
  };

  const showDropdown = open && query.length >= 2;

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          placeholder="Cerca nelle discussioni..."
          className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-full placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
          aria-label="Cerca nelle discussioni"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          // eslint-disable-next-line jsx-a11y/role-has-required-aria-props -- aria-controls richiederebbe un id sul dropdown: cambio di DOM evitato
          role="combobox"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute z-40 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700"
          role="listbox"
        >
          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm">Ricerca in corso...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-5 text-sm text-gray-500 text-center">
              Nessun risultato per &ldquo;<span className="font-medium text-gray-700 dark:text-gray-300">{query}</span>&rdquo;
            </div>
          ) : (
            results.map((result, index) => (
              <button
                key={result.id}
                role="option"
                aria-selected={index === focusedIndex}
                onClick={() => navigateToResult(result.id)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  index === focusedIndex ? 'bg-gray-50 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 line-clamp-1 flex-1">
                    {highlightText(result.title, query)}
                  </span>
                  <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5">
                    {getRelativeTime(result.createdAt)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {result.areaColor ? (
                    <span
                      className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: result.areaColor + '22', color: result.areaColor }}
                    >
                      {result.areaName}
                    </span>
                  ) : (
                    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                      {result.areaName}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {result.repliesCount} {result.repliesCount === 1 ? 'risposta' : 'risposte'}
                  </span>
                </div>
              </button>
            ))
          )}
          {!loading && results.length > 0 && (
            <button
              onClick={() => {
                closeDropdown();
                navigate(`/area-riservata/community-v2/cerca?q=${encodeURIComponent(query.trim())}`);
              }}
              className="w-full px-4 py-2.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors text-center border-t border-gray-100 dark:border-gray-700"
            >
              Vedi tutti i risultati per "{query}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
